const ALBANIA_PAYROLL_CONFIG = {
  taxVersion: 'albania-2026-v1',
  contributionBaseMin: 40000,
  contributionBaseMax: 186416,
  employeeContributionRate: 0.095,
  employeeHealthRate: 0.017,
  taxBrackets: [
    { min: 0, max: 60000, rate: 0, deduction: 0, fixedAmount: 0 },
    { min: 60000, max: 200000, rate: 0.13, deduction: 60000, fixedAmount: 0 },
    {
      min: 200000,
      max: Number.MAX_SAFE_INTEGER,
      rate: 0.23,
      deduction: 200000,
      fixedAmount: 18200
    }
  ]
}

function toPayrollNumber(value) {
  const parsed = parseFloat(value)
  return Number.isFinite(parsed) ? parsed : 0
}

function roundWholeLek(value) {
  return Math.round(toPayrollNumber(value))
}

function calculateAlbaniaTaxFromBrackets(taxableIncome) {
  if (taxableIncome <= 0) {
    return 0
  }

  for (const bracket of ALBANIA_PAYROLL_CONFIG.taxBrackets) {
    if (taxableIncome > bracket.min && taxableIncome <= bracket.max) {
      return Math.max(
        0,
        roundWholeLek(
          bracket.fixedAmount +
            (taxableIncome - bracket.deduction) * bracket.rate
        )
      )
    }
  }

  const lastBracket =
    ALBANIA_PAYROLL_CONFIG.taxBrackets[
      ALBANIA_PAYROLL_CONFIG.taxBrackets.length - 1
    ]

  return Math.max(
    0,
    roundWholeLek(
      lastBracket.fixedAmount +
        (taxableIncome - lastBracket.deduction) * lastBracket.rate
    )
  )
}

function buildAlbaniaPayrollBreakdown({
  grossSalary,
  advancePayment = 0,
  payTax = true,
  payContribution = true,
  pensionDeduction = 0
}) {
  const roundedGrossSalary = roundWholeLek(grossSalary)
  const roundedPensionDeduction = roundWholeLek(
    Math.max(0, toPayrollNumber(pensionDeduction))
  )
  const contributionBase = payContribution
    ? Math.max(
        ALBANIA_PAYROLL_CONFIG.contributionBaseMin,
        Math.min(
          roundedGrossSalary,
          ALBANIA_PAYROLL_CONFIG.contributionBaseMax
        )
      )
    : 0
  const employeeSocialInsurance = payContribution
    ? roundWholeLek(contributionBase * ALBANIA_PAYROLL_CONFIG.employeeContributionRate)
    : 0
  const employeeHealthInsurance = payContribution
    ? roundWholeLek(roundedGrossSalary * ALBANIA_PAYROLL_CONFIG.employeeHealthRate)
    : 0
  const contribution = employeeSocialInsurance + employeeHealthInsurance
  const taxableIncome = roundWholeLek(
    Math.max(0, roundedGrossSalary - contribution - roundedPensionDeduction)
  )
  const totalTax = payTax ? calculateAlbaniaTaxFromBrackets(taxableIncome) : 0

  return {
    contribution_5_percent: contribution,
    tax_0_to_80_0_percent: 0,
    tax_80_to_250_4_percent: 0,
    tax_250_to_450_8_percent: 0,
    tax_greater_450_10_percent: totalTax,
    total_tax: totalTax,
    net_salary: roundWholeLek(
      roundedGrossSalary -
        contribution -
        totalTax -
        roundWholeLek(advancePayment) -
        roundedPensionDeduction
    ),
    pension_deduction: roundedPensionDeduction,
    contribution_base: contributionBase,
    employee_social_insurance: employeeSocialInsurance,
    employee_health_insurance: employeeHealthInsurance,
    taxable_income: taxableIncome,
    payroll_tax_version: ALBANIA_PAYROLL_CONFIG.taxVersion
  }
}

function calculateNetForGross(grossSalary, options) {
  return buildAlbaniaPayrollBreakdown({
    grossSalary,
    payTax: options.payTax,
    payContribution: options.payContribution,
    pensionDeduction: options.pensionDeduction
  }).net_salary
}

function reverseAlbaniaPayrollCalculation(targetNet, options) {
  const normalizedTargetNet = roundWholeLek(targetNet)

  if (normalizedTargetNet <= 0) {
    return {
      contribution: 0,
      bruto: 0,
      total_tax: 0,
      tax_greater_450_10_percent: 0
    }
  }

  let lowerBound = 0
  let upperBound = Math.max(
    ALBANIA_PAYROLL_CONFIG.contributionBaseMin,
    roundWholeLek(normalizedTargetNet * 2 + ALBANIA_PAYROLL_CONFIG.contributionBaseMin)
  )

  while (calculateNetForGross(upperBound, options) < normalizedTargetNet) {
    lowerBound = upperBound
    upperBound *= 2

    if (upperBound > 1000000000) {
      break
    }
  }

  while (lowerBound + 1 < upperBound) {
    const candidate = Math.floor((lowerBound + upperBound) / 2)
    const candidateNet = calculateNetForGross(candidate, options)

    if (candidateNet < normalizedTargetNet) {
      lowerBound = candidate
    } else {
      upperBound = candidate
    }
  }

  const lowerNet = calculateNetForGross(lowerBound, options)
  const upperNet = calculateNetForGross(upperBound, options)
  const resolvedGross =
    Math.abs(upperNet - normalizedTargetNet) <=
    Math.abs(lowerNet - normalizedTargetNet)
      ? upperBound
      : lowerBound
  const result = buildAlbaniaPayrollBreakdown({
    grossSalary: resolvedGross,
    payTax: options.payTax,
    payContribution: options.payContribution,
    pensionDeduction: options.pensionDeduction
  })

  return {
    contribution: result.contribution_5_percent,
    bruto: roundWholeLek(resolvedGross),
    total_tax: result.total_tax,
    tax_greater_450_10_percent: result.tax_greater_450_10_percent
  }
}

export function buildAlbaniaSalaryCalculationPreview({
  grossSalary,
  advancePayment,
  leaveWithoutPayment,
  bonusPayment,
  payTax,
  payContribution,
  pensionDeduction = 0
}) {
  return buildAlbaniaPayrollBreakdown({
    grossSalary:
      toPayrollNumber(grossSalary) -
      toPayrollNumber(leaveWithoutPayment) +
      toPayrollNumber(bonusPayment),
    advancePayment,
    payTax,
    payContribution,
    pensionDeduction
  })
}

export function buildAlbaniaWageCalculationPreview({
  baseGrossSalary,
  advancePayment,
  bonusPayment,
  healthInsuranceCost,
  leaveWithoutPayment,
  payTax,
  payContribution,
  pensionDeduction = 0
}) {
  const grossSalary =
    toPayrollNumber(baseGrossSalary) -
    toPayrollNumber(leaveWithoutPayment) +
    toPayrollNumber(bonusPayment)
  const preview = buildAlbaniaPayrollBreakdown({
    grossSalary,
    advancePayment,
    payTax,
    payContribution,
    pensionDeduction
  })
  const reverseCalculation =
    toPayrollNumber(healthInsuranceCost) > 0
      ? reverseAlbaniaPayrollCalculation(
          preview.net_salary + toPayrollNumber(healthInsuranceCost),
          {
            payTax,
            payContribution,
            pensionDeduction
          }
        )
      : null

  return {
    ...preview,
    contribution_5_percent: reverseCalculation
      ? reverseCalculation.contribution
      : preview.contribution_5_percent,
    health_insurance_gross: reverseCalculation
      ? roundWholeLek(reverseCalculation.bruto - roundWholeLek(grossSalary))
      : 0,
    tax_greater_450_10_percent: reverseCalculation
      ? reverseCalculation.tax_greater_450_10_percent
      : preview.tax_greater_450_10_percent,
    total_salary: roundWholeLek(
      preview.net_salary + toPayrollNumber(healthInsuranceCost)
    ),
    total_tax: reverseCalculation ? reverseCalculation.total_tax : preview.total_tax
  }
}